import React from 'react';
import { CalendarIcon } from '@heroicons/react/24/outline';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';

interface CourseProgressCardProps {
  id: string;
  title: string;
  instructor: string;
  progress: number;
  image: string;
  alt: string;
  nextClass: string;
  totalLessons: number;
  completedLessons: number;
}

export default function CourseProgressCard({
  id,
  title,
  instructor,
  progress,
  image,
  alt,
  nextClass,
  totalLessons,
  completedLessons
}: CourseProgressCardProps) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      {/* Course Image */}
      <div className="relative h-48 w-full overflow-hidden bg-gray-100">
        <AppImage
          src={image}
          alt={alt}
          fill
          className="object-cover"
        />
        <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-md border border-gray-200">
          <span className="text-sm font-semibold text-gray-900">{progress}% Complete</span>
        </div>
      </div>

      {/* Course Info */}
      <div className="p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2 line-clamp-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-4">by {instructor}</p>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-600">Progress</span>
            <span className="text-gray-600">{completedLessons} of {totalLessons} lessons</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Next Class Info */}
        <div className="flex items-center gap-2 mb-4 text-sm text-gray-600">
          <CalendarIcon className="w-4 h-4" />
          <span>Next class: {nextClass}</span>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Link
            href={`/individual-course-page?courseId=${id}`}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-center"
          >
            Continue Learning
          </Link>
          <Link
            href={`/individual-course-page?courseId=${id}`}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium text-center"
          >
            Details
          </Link>
        </div>
      </div>
    </div>
  );
}